/**
 * Générateur — Choisir une représentation adaptée à ce qu'il convient de
 * mettre en avant.
 * Attendu ID : 975 | Niveau : 5ème | Domaine : Statistiques (descripteur 131)
 *
 * Source : doc prof OGD, Statistiques cluster 4 (O5). Plan validé 2026-07-12.
 * Cadrage : le circulaire pour des PARTS dans un tout (repères 50 %, 25 %),
 * les barres pour COMPARER des effectifs, la courbe pour une ÉVOLUTION,
 * le tableau pour garder les VALEURS EXACTES.
 *
 * Cartes :
 *   « L'orientation après la 3e » (Ex.6) — ADAPTATION signalée : la
 *     manipulation du tableur (b) devient un QCM « quel menu » (à vivre en
 *     classe pour le geste réel, comme le doc le prévoit). Tableau des
 *     fréquences avec « la moitié » → 50 % et le reste à déduire (trous),
 *     QCM multiple avantages/inconvénients (les DEUX bonnes annoncées).
 *   « Quelle représentation choisir ? » (Créé n°55) — relier objectif ↔
 *     représentation : un objectif tiré par représentation, formulations
 *     calibrées pour qu'UNE seule association soit défendable (« plus de la
 *     moitié » → circulaire, « évolution au fil de » → courbe, « comparer »
 *     → barres, « valeurs exactes » → tableau). QCM en rotation et classer
 *     (parts d'un tout vs évolution) sur les mêmes pools.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeRelier, makeClasser, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';
import { ligneTable, tableEnonce } from './svgStats';

const ATTENDU_ID = 975;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ORIENTATION = 'L\'orientation après la 3e';
const L_CHOISIR = 'Quelle représentation choisir ?';

// ═════════════════════════════════════════════════════════════
// CARTE « L'orientation après la 3e » (Ex.6)
// ═════════════════════════════════════════════════════════════

const VOIES = ['2de générale et technologique', '2de professionnelle', '1re année de CAP', 'redoublement'];

/** Les trois repères de fraction connus en 5e — le tirage fait TOURNER le
 *  repère (rework 22/07 : la 2de GT était toujours à 50 %, la question
 *  « moitié → 180° » devenait mémorisable). Chaque repère pilote l'intro, la
 *  phrase du drag ET la question du QCM (une seule source de vérité). */
const REPERES = [
    { pct: 50, mot: 'la moitié', verbe: 'va', deg: 180, secteur: 'Un demi-disque', autresMots: ['le quart', 'les trois quarts'], degLeurres: [90, 270] },
    { pct: 25, mot: 'le quart', verbe: 'va', deg: 90, secteur: 'Un quart de disque', autresMots: ['la moitié', 'les trois quarts'], degLeurres: [180, 45] },
    { pct: 75, mot: 'les trois quarts', verbe: 'vont', deg: 270, secteur: 'Les trois quarts du disque', autresMots: ['la moitié', 'le quart'], degLeurres: [180, 90] },
];

/** La 2de GT porte le repère tiré (50, 25 ou 75 %) ; le reste est réparti en
 *  multiples de 5 (≥ 5 %). N multiple de 20 → tout % multiple de 5 donne un
 *  effectif entier. (La contrainte « % tous distincts » de l'ancien format
 *  trous a sauté avec lui : plus rien ne se déduit par élimination.) */
const tirerOrientation = () => {
    const n = 20 * randInt(4, 12);   // 80 à 240 élèves
    const repere = randomChoice(REPERES);
    const reste = 100 - repere.pct;
    for (let essai = 0; essai < 60; essai++) {
        const p2 = 5 * randInt(1, Math.floor(reste / 5) - 2);
        const p3 = 5 * randInt(1, Math.floor((reste - p2) / 5) - 1);
        const p4 = reste - p2 - p3;
        if (p2 < 5 || p3 < 5 || p4 < 5) continue;
        return { n, repere, pcts: [repere.pct, p2, p3, p4] };
    }
    // repli déterministe par repère (sommes vérifiées)
    const repli = { 50: [20, 15, 15], 25: [35, 25, 15], 75: [10, 10, 5] };
    return { n, repere, pcts: [repere.pct, ...repli[repere.pct]] };
};

const introOrientation = (d) =>
    `<p>Dans un collège, on étudie l'orientation des ${d.n} élèves de 3e : `
    + `${d.repere.mot.toUpperCase()} des élèves ${d.repere.verbe} en 2de générale et technologique, `
    + `${d.pcts[1]} % en 2de professionnelle, ${d.pcts[2]} % en 1re année de CAP, et le reste redouble.</p>`;

const tableOrientation = (d) => tableEnonce(
    ligneTable('Voie', [...VOIES, 'Total']),
    ligneTable('Fréquence', [...d.pcts.map((p) => `${p} %`), '100 %']),
);

// Découverte GUIDÉE du choix de représentation (recentrage 22/07 : l'ancien
// texte à trous faisait calculer des fréquences — hors attendu 975). L'élève
// glisse des MOTS : le critère (« d'un coup d'œil » / « valeurs exactes » /
// « comparer ») → la représentation → le repère 50 % ↔ demi-disque. Deux
// gabarits en rotation, leurres = confusions types (quart/90°, mauvais
// diagramme).
const genOrientationDrag = () => {
    const d = tirerOrientation();
    const r = d.repere;
    const contexte = `${introOrientation(d)}${tableOrientation(d)}`
        + '<p>Complète le raisonnement en glissant les bonnes étiquettes.</p>';
    const gabarits = [
        {
            phrase: 'Pour montrer d\'un coup d\'œil que la 2de générale et technologique représente '
                + `{{${r.mot}}} du total, on choisit le diagramme {{circulaire}} : son secteur mesure `
                + `{{${r.deg}}}°. Pour conserver les valeurs exactes et pouvoir refaire des calculs, `
                + 'on garde le {{tableau}}.',
            leurres: [r.autresMots[0], 'en barres', String(r.degLeurres[0])],
        },
        {
            phrase: 'Pour comparer d\'un coup d\'œil les effectifs des quatre voies, on choisit le '
                + 'diagramme {{en barres}}. Pour montrer que la 2de générale et technologique représente '
                + `{{${r.mot}}} du total, on choisit le diagramme {{circulaire}} : son secteur mesure `
                + `{{${r.deg}}}°.`,
            leurres: ['le tableau', r.autresMots[1], String(r.degLeurres[1])],
        },
    ];
    const g = randomChoice(gabarits);
    const distracteurs = filtrerDistracteurs(g.phrase, g.leurres).slice(0, 3);
    return makeTexteTrou(g.phrase, contexte,
        { ...opts(L_ORIENTATION), mode: 'drag', distracteurs });
};

// Rotation de questions toutes centrées sur le CHOIX (la variante « quel menu
// du tableur » est supprimée : culture logicielle, pas l'attendu — le geste
// tableur se vit en classe, comme le doc le prévoit).
// Les 3 descriptions de secteur (les repères) + le leurre « disque entier » —
// la bonne réponse suit le repère tiré, les autres servent de distracteurs.
const SECTEURS = {
    90: 'Un quart de disque : son secteur mesure 90°',
    180: 'Un demi-disque : son secteur mesure 180°',
    270: 'Les trois quarts du disque : son secteur mesure 270°',
};

const genOrientationQcm = () => {
    const d = tirerOrientation();
    const r = d.repere;
    const contexte = `${introOrientation(d)}${tableOrientation(d)}`;
    const questions = [
        () => {
            const bonne = SECTEURS[r.deg];
            const autres = Object.keys(SECTEURS).filter((deg) => Number(deg) !== r.deg)
                .map((deg) => SECTEURS[deg]);
            return makeQcm([bonne, ...autres, 'Le disque entier : son secteur mesure 360°'], bonne,
                contexte + '<p>On représente ces données par un diagramme circulaire. À quoi ressemblera '
                + `le secteur de la 2de générale et technologique (${r.mot} des élèves) ?</p>`,
                opts(L_ORIENTATION));
        },
        () => {
            const bonne = 'Le diagramme circulaire';
            return makeQcm([bonne, 'Le diagramme en barres', 'La courbe (graphique cartésien)',
                'La liste de toutes les réponses des élèves'], bonne,
                contexte + `<p>On veut montrer D'UN COUP D'ŒIL que la 2de générale et technologique `
                + `représente ${r.mot} du total. Quelle représentation choisir ?</p>`,
                opts(L_ORIENTATION));
        },
    ];
    return randomChoice(questions)();
};

// ═════════════════════════════════════════════════════════════
// CARTE « Quelle représentation choisir ? » (Créé n°55)
// ═════════════════════════════════════════════════════════════

const REPRESENTATIONS = {
    circulaire: 'Diagramme circulaire',
    barres: 'Diagramme en barres',
    courbe: 'Courbe (graphique cartésien)',
    tableau: 'Tableau de données',
};

// Chaque objectif renvoie à un exercice du thème (l'élève choisit APRÈS avoir
// pratiqué, comme le veut le cadrage) — une seule association défendable.
const OBJECTIFS = {
    circulaire: [
        'Montrer que les fruits forestiers représentent plus de la moitié du régime du sanglier',
        'Voir si un type de canapé représente plus du quart du stock du magasin',
        'Visualiser la part de chaque voie d\'orientation dans le total des élèves',
        'Montrer qu\'un sport regroupe à lui seul plus de la moitié des licenciés',
        'Visualiser la part de chaque poste de dépenses dans un budget',
        'Voir d\'un coup d\'œil si les transports représentent plus du quart des dépenses',
        'Montrer que les romans représentent plus de la moitié des ventes de la librairie',
        'Voir si les mangas représentent plus du quart des emprunts du CDI',
    ],
    barres: [
        'Comparer les précipitations des 12 mois de l\'année à Brest',
        'Comparer les effectifs de chaque pointure dans un magasin de chaussures',
        'Comparer les ventes des cinq types d\'articles d\'une boutique',
        'Comparer le nombre de médailles de six pays aux Jeux olympiques',
        'Comparer les effectifs des réponses « jamais », « parfois », « souvent » d\'un sondage',
        'Comparer la fréquentation de quatre cantines un même jour',
        'Comparer le nombre de buts marqués par six équipes d\'un tournoi',
        'Comparer le nombre de livres lus par cinq classes pendant l\'année',
    ],
    courbe: [
        'Suivre l\'évolution de la température au fil de la semaine',
        'Suivre l\'évolution de la taille d\'une plante jour après jour',
        'Suivre l\'évolution du nombre d\'abonnés d\'une chaîne au fil des mois',
        'Suivre l\'évolution du niveau d\'une rivière heure par heure pendant une crue',
        'Suivre l\'évolution de la masse d\'un chaton semaine après semaine',
        'Suivre l\'évolution du prix d\'un jeu vidéo au fil des années',
        'Suivre l\'évolution du nombre de visiteurs d\'un zoo mois par mois',
        'Suivre l\'évolution de la hauteur de neige au fil de la semaine',
    ],
    tableau: [
        'Conserver les valeurs exactes des ventes pour refaire des calculs plus tard',
        'Retrouver l\'effectif précis de chaque catégorie sans lecture graphique',
        'Garder toutes les données chiffrées pour pouvoir vérifier un total',
        'Transmettre les résultats bruts d\'une enquête pour un traitement au tableur',
        'Noter les effectifs exacts au fur et à mesure d\'un comptage',
        'Chercher la valeur précise d\'une donnée sans risque d\'erreur de lecture',
        'Garder le détail exact des scores pour établir un classement plus tard',
        'Conserver les effectifs précis de chaque réponse pour préparer un exposé',
    ],
};

const CLES_REP = Object.keys(REPRESENTATIONS);

const genChoisirRelier = () => {
    const pairs = CLES_REP.map((cle) => ({
        source: randomChoice(OBJECTIFS[cle]),
        target: REPRESENTATIONS[cle],
    }));
    return makeRelier(pairs,
        '<p>Pour chaque objectif, choisis la représentation LA PLUS adaptée.</p>',
        opts(L_CHOISIR));
};

const genChoisirQcm = () => {
    const cle = randomChoice(CLES_REP);
    const bonne = REPRESENTATIONS[cle];
    return makeQcm(Object.values(REPRESENTATIONS), bonne,
        `<p>Objectif : « ${randomChoice(OBJECTIFS[cle])} ».</p>`
        + '<p>Quelle représentation est LA PLUS adaptée ?</p>',
        opts(L_CHOISIR));
};

const genChoisirClasser = () => {
    const [repA, repB] = shuffle([
        ['circulaire', 'Objectifs pour un diagramme circulaire'],
        ['courbe', 'Objectifs pour une courbe'],
        ['barres', 'Objectifs pour un diagramme en barres'],
    ]).slice(0, 2);
    return makeClasser(
        [{ name: repA[1], items: shuffle(OBJECTIFS[repA[0]]).slice(0, 3) },
         { name: repB[1], items: shuffle(OBJECTIFS[repB[0]]).slice(0, 3) }],
        '<p>Classe chaque objectif selon la représentation la plus adaptée.</p>',
        opts(L_CHOISIR));
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    orientation_texte_trou_drag: genOrientationDrag,
    orientation_qcm_unique: genOrientationQcm,
    choisir_relier: genChoisirRelier,
    choisir_qcm_unique: genChoisirQcm,
    choisir_classer: genChoisirClasser,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'orientation', label: L_ORIENTATION, formatsList: buildFormats('orientation') },
    { id: 'choisir', label: L_CHOISIR, formatsList: buildFormats('choisir') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
